import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import {
  MdAddCircleOutline,
  MdEdit,
  MdLogout,
  MdOutlineDeleteForever,
  MdOutlineFaceRetouchingOff,
  MdOutlineGroupAdd,
  MdOutlineGroups2,
  MdOutlineLockReset,
  MdSearch,
} from "react-icons/md";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { ReactComponent as BluePenIcon } from "../../../assets/blue_pen_icon.svg";
import { ReactComponent as CertificateIcon } from "../../../assets/cerificate_icon.svg";
import cup_icon from "../../../assets/cup_icon.svg";
import { ReactComponent as GreenCheckIcon } from "../../../assets/green_check_icon.svg";
import { ReactComponent as RedCloseIcon } from "../../../assets/red_close_icon.svg";
import { ReactComponent as ClassroomReportsIcon } from "../../../assets/Subtraction3.svg";
import Button from "../../../components/Button";
import CustomDataTable from "../../../components/CustomDataTable";
import LoadingSpinner from "../../../components/LoadingSpinner";
import AssignMultiStudentsModal from "../../../components/modals/common/AssignMultiStudentsModal";
import AssignStudentModal from "../../../components/modals/common/AssignStudentModal";
import DeleteClassroomModal from "../../../components/modals/common/DeleteClassroomModal";
import EditClassroomModal from "../../../components/modals/common/EditClassroomModal";
import EditStudentModal from "../../../components/modals/common/EditStudentModal";
import LogoutStudentModal from "../../../components/modals/common/LogoutStudentModal";
import UnassignStudentModal from "../../../components/modals/common/UnassignStudentModal";
import AddStudentModal from "../../../components/modals/school/AddStudentModal";
import CompletionCertificateModal from "../../../components/modals/teacher/CompletionCertificateModal";
import FinishLessonsModal from "../../../components/modals/teacher/FinishLessonsModal";
import ResetChallengesModal from "../../../components/modals/teacher/ResetChallengesModal";
import ResetLessonQuestionsModal from "../../../components/modals/teacher/ResetLessonQuestionsModal";
import TopFiveStudentsModal from "../../../components/modals/teacher/TopFiveStudentsModal";
import UnlockLessonsModal from "../../../components/modals/teacher/UnlockLessonsModal";
import TableDropdownMenu from "../../../components/TableDropdownMenu";
import { USER_TYPE } from "../../../constants/common";
import { resetBreadcrumbs, setBreadcrumbDynamicPathNames } from "../../../redux/slices/breadcrumbsSlice";
import classroomServices from "../../../services/classroomServices";
import { getLanguage } from "../../../utils/getLanguage";
import { useNavigateWithLang } from "../../../utils/navigation";

const TeacherClassroomDetails = () => {
  const { id: classroomID } = useParams();
  const navigate = useNavigateWithLang();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const lang = getLanguage();

  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [classroomDetails, setClassroomDetails] = useState(null);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [classroomStats, setClassroomStats] = useState("");
  const [projectStats, setProjectStats] = useState(null);

  const assignStudentModalRef = useRef();
  const unassignStudentModalRef = useRef();
  const assignMultiStudentsModalRef = useRef();
  const deleteClassroomModalRef = useRef();
  const logoutStudentModalRef = useRef();
  const editModalRef = useRef();
  const editStudentModalRef = useRef();
  const resetChallengesModalRef = useRef();
  const resetLessonQuestionsModalRef = useRef();
  const finishLessonsModalRef = useRef();
  const unlockLessonsModalRef = useRef();
  const completionCertificateModalRef = useRef();
  const addStudentModalRef = useRef();
  const topStudentsModalRef = useRef();

  const openTopStudentsModal = (classroom) => topStudentsModalRef.current.openModal(classroom);
  const openAddStudentModal = (classroom) => addStudentModalRef.current.openModal(classroom);
  const openCompletionCertificateModal = (classroom, lesson) =>
    completionCertificateModalRef.current.openModal(classroom, lesson);
  const openUnlockLessonsModal = (classroom, lessons) =>
    unlockLessonsModalRef.current.openModal(classroom, lessons);
  const openFinishLessonsModal = (classroom, lessons, students) =>
    finishLessonsModalRef.current.openModal(classroom, lessons, students);
  const openAssignStudentModal = (student) => assignStudentModalRef.current.openModal(student);
  const openUnassignStudentModal = (student) => unassignStudentModalRef.current.openModal(student);
  const openAssignMultiStudentsModal = (classroom) =>
    assignMultiStudentsModalRef.current.openModal(classroom);
  const openDeleteClassroomModal = (classroom) => deleteClassroomModalRef.current.openModal(classroom);
  const openLogoutStudentsModal = (classroom) => logoutStudentModalRef.current.openModal(classroom);
  const openEditModal = (classroom) => editModalRef.current.openModal(classroom);
  const openEditStudentModal = (student) => editStudentModalRef.current.openModal(student);
  const openResetChallengesModal = (classroomDetails, students) =>
    resetChallengesModalRef.current.openModal(classroomDetails, students);
  const openResetLessonQuestionsModal = (classroomDetails, students) =>
    resetLessonQuestionsModalRef.current.openModal(classroomDetails, students);

  const handleAssignStudentSuccess = () => {
    getClassroomStudents();
    getClassroomDetails();
  };

  const handleAddStudentsSuccess = () => {
    getClassroomStudents();
    getClassroomDetails();
  };

  const handleUnassignStudentSuccess = () => {
    getClassroomStudents();
    getClassroomDetails();
  };
  const handleEditStudentSuccess = () => {
    getClassroomStudents();
  };
  const handleUnlockLessonsSuccess = () => {
    // getClassroomDetails();
  };

  const handleCompletionCertificateSuccess = () => {
    // getClassroomDetails();
  };

  const handleFinishLessonsSuccess = () => {
    // getStudentDetails();
  };
  const handleResetChallengesSuccess = () => {
    // getStudentDetails();
  };
  const handleResetLessonQuestionsSuccess = () => {
    // getStudentDetails();
  };

  const normalizeText = (text) => {
    return text.trim().replace(/\s+/g, " ").toLowerCase();
  };

  const handleSearch = (event) => {
    const rawInput = event.target.value;
    setSearchText(rawInput);

    const normalizedQuery = normalizeText(rawInput);

    const filtered = students.filter((student) =>
      normalizeText(student.student.name).includes(normalizedQuery)
    );

    setFilteredStudents(filtered);
  };

  const columns = [
    {
      name: t("student_name"),
      field: "student.name",
      sortable: true,
      body: (rowData) => (
        <Button
          tooltip={rowData.student.name}
          labelStyle="block w-44 overflow-hidden truncate whitespace-nowrap text-start"
          label={rowData.student.name}
          variant="link"
          color="blue"
          onClick={() => navigateStudentDetails(rowData.student.id)}
        />
      ),
    },
    {
      name: t("username"),
      field: "student.username",
      sortable: true,
    },
    {
      name: t("skills_score"),
      field: "skill_score",
      sortable: true,
    },
    {
      name: t("total_points"),
      field: "student.total_points",
      sortable: true,
    },
    {
      name: "",
      body: (rowData) => (
        <TableDropdownMenu
          items={[
            {
              label: "edit_student_information",
              icon: <MdEdit className="text-blue-500" size={19} />,
              onClick: () => openEditStudentModal(rowData.student),
            },
            {
              label: "reset_password",
              icon: <MdOutlineLockReset className="text-blue-500" size={20} />,
              onClick: () => openEditStudentModal(rowData.student),
            },
            {
              label: "remove_from_classroom",
              icon: <MdOutlineFaceRetouchingOff className="text-gray-600" size={20} />,
              onClick: () => openUnassignStudentModal(rowData.student),
            },
            {
              label: "assign_to_classroom",
              icon: <MdOutlineGroups2 className="text-[#FF7F50]" size={20} />,
              onClick: () => openAssignStudentModal(rowData.student),
            },
          ]}
        />
      ),
    },
  ];

  const getClassroomDetails = async () => {
    try {
      const response = await classroomServices.getClassroomDetails(classroomID);
      const response2 = await classroomServices.getClassroomStats(classroomID);
      setClassroomDetails(response);
      setClassroomStats(response2);
      dispatch(setBreadcrumbDynamicPathNames([response?.classroom?.name]));
    } catch (error) {
      console.error("Error fetching classroom details:", error);
    } finally {
      setLoading(false);
    }
  };

  const getClassroomStudents = async () => {
    try {
      const res = await classroomServices.getStudentsByClassroomID(classroomID, {
        per_page: 10,
        order_dir: "asc",
      });
      setStudents(res.data);

      if (searchText) {
        const filtered = res.data.filter((student) =>
          student.student.name.toLowerCase().includes(searchText)
        );
        setFilteredStudents(filtered);
      } else {
        setFilteredStudents(res.data);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
    }
  };
  const getClassroomProjectStats = async () => {
    try {
      const res = await classroomServices.classroomProjectsStats(classroomID);
      setProjectStats(res);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
    }
  };

  useEffect(() => {
    getClassroomDetails();
    getClassroomStudents();
    getClassroomProjectStats();
  }, [lang]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    return () => {
      dispatch(resetBreadcrumbs());
    };
  }, []);

  const navigateStudentDetails = (id) => {
    navigate(`/${USER_TYPE.TEACHER.ROLE}/my-classrooms/${classroomDetails?.classroom?.id}/${id}`);
  };
  const navigateCodeAssessment = (id, programType) => {
    localStorage.setItem("selectedClassroomId", id);
    localStorage.setItem("selectedProgramType", programType);
    navigate(`/${USER_TYPE.TEACHER.ROLE}/code-assessment-by-lesson`);
  };

  const navigateProjectAssessment = (id) => {
    localStorage.setItem("selectedClassroomId", id);
    navigate(`/${USER_TYPE.TEACHER.ROLE}/project-assessment-by-lesson`);
  };

  const navigateMaterialsAndResources = (id) => {
    localStorage.setItem("selectedCourseId", id);
    navigate(`/${USER_TYPE.TEACHER.ROLE}/materials-resources`);
  };
  const handleAssignStudents = () => {
    getClassroomStudents();
  };

  const handleDeleteClassroomSuccess = () => {
    navigate(`/${USER_TYPE.TEACHER.ROLE}/my-classrooms`);
  };
  const handleLogoutStudentSuccess = () => {};

  const handleEditClassroomSuccess = () => {
    getClassroomDetails();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between lg:space-x-2">
        <div className="flex flex-wrap items-center justify-start lg:space-x-2">
          <Button
            iconLeft={<MdOutlineGroupAdd size={20} />}
            color="yellow"
            label={t("assign_students")}
            onClick={() => openAssignMultiStudentsModal(classroomDetails?.classroom)}
          />
          <Button
            iconLeft={<MdAddCircleOutline size={20} />}
            label={t("add_new_student")}
            onClick={() => openAddStudentModal(classroomDetails?.classroom)}
            color="green"
          />
          <Button
            iconLeft={<MdOutlineDeleteForever size={20} />}
            label={`${t("delete_classroom")}`}
            color="red"
            onClick={() => openDeleteClassroomModal(classroomDetails)}
          />
        </div>
        <Button
          iconLeft={<MdLogout size={20} />}
          onClick={() => openLogoutStudentsModal(classroomDetails)}
          label={t("logout_all_students")}
          color="orange"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
        <div className="col-span-12 grid grid-cols-1 gap-4">
          <div className="col-span-12 items-center">
            <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface">
              <div>
                <div className="flex items-center">
                  <h3 className="text-xl font-bold capitalize text-blue-600 dark:text-blue-400">
                    {classroomDetails?.classroom?.name} - {classroomDetails?.classroom?.section} -{" "}
                    {classroomDetails?.grade?.name}
                  </h3>
                  <Button
                    className="mx-1"
                    variant="text"
                    icon={<FaEdit size={20} />}
                    onClick={() => openEditModal(classroomDetails)}
                    tooltip={t("edit_classroom")}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <Button
                  variant="text"
                  icon={<img src={cup_icon} className="w-8" alt="cup" />}
                  onClick={() => openTopStudentsModal(classroomDetails)}
                  tooltip={t("top_five_students")}
                />
              </div>
            </div>
          </div>

          <div className="col-span-12 grid grid-cols-1 gap-4 md:grid-cols-12">
            {/* Classroom Report Section */}
            <div
              onClick={() => navigateMaterialsAndResources(classroomDetails?.course?.id)}
              className="col-span-12 cursor-pointer overflow-hidden rounded-xl bg-white shadow-lg transition duration-300 hover:scale-[1.02] dark:bg-dark-surface lg:col-span-6 xl:col-span-2"
              title={t(`go_to_materials_and_resources`)}
            >
              {/* Top image section */}
              <img src={classroomDetails?.course?.image} alt="Course" className="h-36 w-full object-cover" />

              {/* Bottom content section */}
              <div className="flex flex-col justify-end gap-1 bg-white p-4 dark:bg-dark-surface">
                <h4 className="w-fit bg-[#842880] px-2 text-sm uppercase text-white dark:text-gray-300 rtl:w-20">
                  {t("course")}
                </h4>
                <h3 className="text-lg font-semibold text-[#842880] dark:text-white">
                  {classroomDetails?.course?.course}
                </h3>
              </div>
            </div>

            {/* Total Course Challenges / Projects */}
            <div
              className={`col-span-12 flex flex-col justify-end rounded-xl bg-white p-3 shadow-md dark:bg-dark-surface lg:col-span-6 ${classroomDetails?.course?.for_children ? "xl:col-span-4" : "xl:col-span-3"}`}
            >
              <div className="px-3">
                <h3 className="font-semibold text-mutedText dark:text-dark-mutedText">
                  {classroomDetails?.course?.for_children
                    ? t("total_course_project")
                    : t("total_course_challenge")}
                </h3>
                <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-white">
                  {classroomDetails?.course?.for_children
                    ? (projectStats?.approved_challenges || 0) +
                      (projectStats?.pending_challenges || 0) +
                      (projectStats?.rejected_challenges || 0)
                    : (projectStats?.approved_challenges || 0) +
                      (projectStats?.pending_challenges || 0) +
                      (projectStats?.rejected_challenges || 0)}
                </p>
                <div className="my-4">
                  <p className="mb-2 flex font-medium text-blue-600">
                    <span className="flex flex-1 items-center gap-2">
                      <BluePenIcon />
                      {t("pending")}
                    </span>
                    <span className="flex flex-1 justify-end font-medium">
                      {projectStats?.pending_challenges || 0}
                    </span>
                  </p>
                  <p className="mb-2 flex font-medium text-green-500">
                    <span className="flex-2 flex items-center gap-2">
                      <GreenCheckIcon />
                      {t("approved")}
                    </span>
                    <span className="flex flex-1 justify-end font-medium">
                      {projectStats?.approved_challenges || 0}
                    </span>
                  </p>
                  <p className="mb-2 flex font-medium text-red-500">
                    <span className="flex flex-1 items-center gap-2">
                      <RedCloseIcon />
                      {t("rejected")}
                    </span>
                    <span className="flex flex-1 justify-end font-medium">
                      {projectStats?.rejected_challenges || 0}
                    </span>
                  </p>
                </div>
              </div>
              <Button
                variant="outlined"
                className="w-full"
                label={
                  classroomDetails?.course?.for_children ? t("project_assessment") : t("code_assessment")
                }
                onClick={
                  classroomDetails?.course?.for_children
                    ? () => navigateProjectAssessment(classroomDetails?.classroom?.id)
                    : () => navigateCodeAssessment(classroomDetails?.classroom?.id, "challenge")
                }
              />
            </div>

            {/* Playground Projects (only for non-children courses) */}
            {!classroomDetails?.course?.for_children && (
              <div className="col-span-12 flex flex-col justify-end rounded-xl bg-white p-3 shadow-md dark:bg-dark-surface lg:col-span-6 xl:col-span-3">
                <div className="px-3">
                  <h3 className="font-semibold text-mutedText dark:text-dark-mutedText">
                    {t("playground_projects")}
                  </h3>
                  <p className="mt-1 text-3xl font-bold text-gray-800 dark:text-white">
                    {(projectStats?.approved_playground || 0) +
                      (projectStats?.pending_playground || 0) +
                      (projectStats?.rejected_playground || 0)}
                  </p>
                  <div className="my-4">
                    <p className="mb-2 flex font-medium text-blue-600">
                      <span className="flex flex-1 items-center gap-2">
                        <BluePenIcon />
                        {t("pending")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {projectStats?.pending_playground || 0}
                      </span>
                    </p>
                    <p className="mb-2 flex font-medium text-green-500">
                      <span className="flex-2 flex items-center gap-2">
                        <GreenCheckIcon />
                        {t("approved")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {projectStats?.approved_playground || 0}
                      </span>
                    </p>
                    <p className="mb-2 flex font-medium text-red-500">
                      <span className="flex flex-1 items-center gap-2">
                        <RedCloseIcon />
                        {t("rejected")}
                      </span>
                      <span className="flex flex-1 justify-end font-medium">
                        {projectStats?.rejected_playground || 0}
                      </span>
                    </p>
                  </div>
                </div>
                <Button
                  className="w-full"
                  variant="outlined"
                  label={t("code_assessment")}
                  onClick={() => navigateCodeAssessment(classroomDetails?.classroom?.id, "playground")}
                />
              </div>
            )}

            {/* Classroom Report Section */}
            <div
              className={`col-span-12 flex flex-col items-center overflow-hidden rounded-xl bg-white p-3 shadow-lg dark:bg-dark-surface lg:col-span-12 ${classroomDetails?.course?.for_children ? "xl:col-span-3" : "xl:col-span-2"}`}
            >
              <div className="flex flex-1 flex-col items-center justify-center text-center">
                <ClassroomReportsIcon className="mb-5 h-16 w-16 text-blue-600" />
                <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
                  {t("classroom_report")}
                </h3>
              </div>
              <Button
                variant="outlined"
                className="w-full"
                label={t("go_to_reports")}
                onClick={() => navigate(`/${USER_TYPE.TEACHER.ROLE}/classroom-reports?classroomId=${2}`)}
              />
            </div>

            {/* Classroom Report Section */}
            <div
              className={`col-span-12 flex flex-col items-center overflow-hidden rounded-xl bg-white p-3 shadow-lg dark:bg-dark-surface lg:col-span-12 ${classroomDetails?.course?.for_children ? "xl:col-span-3" : "xl:col-span-2"}`}
            >
              <div className="flex flex-1 flex-col items-center justify-center px-4">
                <CertificateIcon className="mb-4 h-14 w-14 text-mutedText dark:text-dark-mutedText" />
                <h3 className="text-center font-semibold text-mutedText dark:text-dark-mutedText">
                  {t("completion_certificate")}
                </h3>
              </div>
              <Button
                disabled
                className="w-full"
                variant="outlined"
                label={t("generate")}
                onClick={() => openCompletionCertificateModal(classroomDetails?.classroom)}
              />
            </div>
          </div>
        </div>
        <div className="col-span-12 grid items-center gap-4">
          <div className="flex items-center justify-between rounded-xl bg-white p-4 shadow-md dark:bg-dark-surface">
            <h3 className="font-medium text-blue-600 dark:text-blue-400">
              <div className="flex space-x-1 font-bold">
                <p className="text-slate-800 dark:text-white">{t("last_completed_lesson")}:</p>
                <p className="text-blue-400">{classroomStats?.last_completed_lesson}</p>
                <p className="text-blue-400"> - </p>
                <p className="text-blue-400">{classroomStats?.completed_lessons_count}</p>
                <p className="text-blue-400">/</p>
                <p className="text-blue-400">{classroomStats?.lesson_count}</p>
              </div>
            </h3>

            <p className="font-medium text-mutedText dark:text-dark-mutedText">
              <div className="flex space-x-1 font-bold">
                <p className="text-slate-800 dark:text-white">{t("following_students")}:</p>
                <p className="text-blue-400">{classroomStats?.students_finished}</p>
                <p className="text-blue-400">/</p>
                <p className="text-blue-400">{classroomStats?.students_count}</p>
              </div>
            </p>
          </div>
        </div>
        <div className="col-span-12 rounded-xl bg-white shadow-md dark:bg-dark-surface">
          <CustomDataTable
            columns={columns}
            data={filteredStudents}
            withPagination
            withSort
            header={
              <p className="text-right text-sm font-semibold rtl:text-left">
                <span className="text-slate-800 dark:text-white">{t("current_students")}: </span>
                <span className="text-blue-400"> {classroomDetails?.student_count}</span>
              </p>
            }
            search={
              <div className="relative w-1/3">
                <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xl text-mutedText dark:text-dark-mutedText rtl:left-auto rtl:right-3" />
                <input
                  type="text"
                  placeholder={t("search")}
                  value={searchText}
                  onChange={handleSearch}
                  className="w-full rounded-md border bg-white py-3 pl-10 pr-3 text-sm placeholder:text-mutedText focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-white rtl:pl-3 rtl:pr-10"
                />
              </div>
            }
            actions={
              students.length > 0 ? (
                <>
                  <Button
                    onClick={() =>
                      openUnlockLessonsModal(classroomDetails?.classroom, classroomDetails?.lessons)
                    }
                    label={t("unlock_lessons")}
                  />
                  <Button
                    onClick={() =>
                      openFinishLessonsModal(classroomDetails?.classroom, classroomDetails?.lessons, students)
                    }
                    label={t("finish_lessons")}
                  />
                  <Button
                    onClick={() => openResetLessonQuestionsModal(classroomDetails, filteredStudents)}
                    label={t("reset_questions")}
                  />
                  <Button
                    onClick={() => openResetChallengesModal(classroomDetails, filteredStudents)}
                    label={
                      classroomDetails?.course?.for_children ? t("reset_projects") : t("reset_challenges")
                    }
                  />
                </>
              ) : null
            }
          />
        </div>
      </div>

      {/* Modals */}
      <AssignStudentModal ref={assignStudentModalRef} onSubmit={handleAssignStudentSuccess} />
      <UnassignStudentModal ref={unassignStudentModalRef} onSubmit={handleUnassignStudentSuccess} />
      <AssignMultiStudentsModal ref={assignMultiStudentsModalRef} onSubmit={handleAssignStudentSuccess} />
      <DeleteClassroomModal ref={deleteClassroomModalRef} onSubmit={handleDeleteClassroomSuccess} />
      <LogoutStudentModal ref={logoutStudentModalRef} onSubmit={handleLogoutStudentSuccess} />
      <EditClassroomModal ref={editModalRef} onSubmit={handleEditClassroomSuccess} />
      <EditStudentModal ref={editStudentModalRef} onSubmit={handleEditStudentSuccess} />
      <AddStudentModal ref={addStudentModalRef} onSubmit={handleAddStudentsSuccess} />

      <TopFiveStudentsModal ref={topStudentsModalRef} />
      <UnlockLessonsModal
        ref={unlockLessonsModalRef}
        onSubmit={handleUnlockLessonsSuccess}
        getClassroomDetails={getClassroomDetails}
      />
      <CompletionCertificateModal
        ref={completionCertificateModalRef}
        onSubmit={handleCompletionCertificateSuccess}
      />
      <FinishLessonsModal
        ref={finishLessonsModalRef}
        onSubmit={handleFinishLessonsSuccess}
        type="classroom"
      />
      <ResetChallengesModal
        ref={resetChallengesModalRef}
        onSubmit={handleResetChallengesSuccess}
        type="classroom"
      />
      <ResetLessonQuestionsModal
        ref={resetLessonQuestionsModalRef}
        onSubmit={handleResetLessonQuestionsSuccess}
        type="classroom"
      />
    </>
  );
};

export default TeacherClassroomDetails;
